import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Edit, Trash2, ChevronUp, ChevronDown, Star } from "lucide-react";
import { getApiErrorPayload, orderChannelsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Loading } from "@/components/ui/loader";
import { ORDER_TYPE_COLORS } from "./order-type-icons";
import { ChannelBrandMark } from "./ChannelBrandMark";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface OrderChannel {
	id: number;
	nameEn: string;
	nameAr: string;
	logo: string | null;
	color: string | null;
	channelType: "in_store" | "third_party";
	provider: string | null;
	isActive: boolean;
	isDefault: boolean;
	sortOrder: number;
	branchId: number;
}

interface ChannelForm {
	nameEn: string;
	nameAr: string;
	logo: string;
	color: string;
	channelType: "in_store" | "third_party";
	provider: string;
	isActive: boolean;
	isDefault: boolean;
}

const emptyForm: ChannelForm = {
	nameEn: "",
	nameAr: "",
	logo: "",
	color: "#FF5A00",
	channelType: "third_party",
	provider: "",
	isActive: true,
	isDefault: false,
};

/** Branch-scoped order-channel CRUD (In-store + third-party delivery platforms). Embedded in the POS detail page. */
export function OrderChannelsManager({ branchId }: { branchId: number }) {
	const { t, i18n } = useTranslation();
	const isAr = i18n.language === "ar";
	const { toast } = useToast();
	const [items, setItems] = useState<OrderChannel[]>([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<OrderChannel | null>(null);
	const [form, setForm] = useState<ChannelForm>(emptyForm);
	const [saving, setSaving] = useState(false);

	const showError = useCallback(
		(error: unknown) => {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		},
		[t, toast],
	);

	const fetchItems = useCallback(() => {
		setLoading(true);
		orderChannelsAPI
			.getAll(branchId)
			.then((data: OrderChannel[]) => setItems(data))
			.catch(showError)
			.finally(() => setLoading(false));
	}, [branchId, showError]);

	useEffect(() => {
		fetchItems();
	}, [fetchItems]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setDialogOpen(true);
	};

	const openEdit = (c: OrderChannel) => {
		setEditing(c);
		setForm({
			nameEn: c.nameEn,
			nameAr: c.nameAr,
			logo: c.logo ?? "",
			color: c.color ?? "#FF5A00",
			channelType: c.channelType,
			provider: c.provider ?? "",
			isActive: c.isActive,
			isDefault: c.isDefault,
		});
		setDialogOpen(true);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSaving(true);
		const payload = {
			branchId,
			nameEn: form.nameEn.trim(),
			nameAr: form.nameAr.trim(),
			logo: form.logo.trim() || undefined,
			color: form.color || undefined,
			channelType: form.channelType,
			provider: form.channelType === "third_party" ? form.provider.trim() || undefined : undefined,
			isActive: form.isActive,
			isDefault: form.isDefault,
		};
		try {
			if (editing) {
				await orderChannelsAPI.update(editing.id, payload);
				toast({ title: t("common.success"), description: t("orderChannelsAdmin.messages.updated") });
			} else {
				await orderChannelsAPI.create(payload);
				toast({ title: t("common.success"), description: t("orderChannelsAdmin.messages.created") });
			}
			setDialogOpen(false);
			fetchItems();
		} catch (error) {
			showError(error);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (c: OrderChannel) => {
		if (
			!(await confirmAction(t("orderChannelsAdmin.confirmDelete", { name: isAr ? c.nameAr : c.nameEn }), {
				confirmLabel: t("common.delete"),
			}))
		)
			return;
		try {
			await orderChannelsAPI.delete(c.id);
			toast({ title: t("common.success"), description: t("orderChannelsAdmin.messages.deleted") });
			fetchItems();
		} catch (error) {
			showError(error);
		}
	};

	const move = async (index: number, dir: -1 | 1) => {
		const next = index + dir;
		if (next < 0 || next >= items.length) return;
		const reordered = [...items];
		[reordered[index], reordered[next]] = [reordered[next], reordered[index]];
		setItems(reordered);
		try {
			await orderChannelsAPI.reorder(
				branchId,
				reordered.map(c => c.id),
			);
		} catch (error) {
			showError(error);
			fetchItems();
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-end gap-2">
				<PermissionGate permission="settings.create">
					<Button type="button" size="sm" onClick={openCreate}>
						<Plus className="me-2 h-4 w-4" />
						{t("orderChannelsAdmin.add")}
					</Button>
				</PermissionGate>
			</div>

			{loading ? (
				<Loading />
			) : items.length === 0 ? (
				<p className="text-muted-foreground py-8 text-center text-sm">{t("orderChannelsAdmin.empty")}</p>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-24">{t("orderChannelsAdmin.table.order")}</TableHead>
							<TableHead>{t("orderChannelsAdmin.table.name")}</TableHead>
							<TableHead className="text-center">{t("orderChannelsAdmin.table.type")}</TableHead>
							<TableHead className="text-center">{t("orderChannelsAdmin.table.default")}</TableHead>
							<TableHead className="text-center">{t("orderChannelsAdmin.table.status")}</TableHead>
							<TableHead className="text-end">{t("orderChannelsAdmin.table.actions")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{items.map((c, index) => (
							<TableRow key={c.id}>
								<TableCell>
									<div className="flex items-center gap-1">
										<Button
											title={t("orderChannelsAdmin.moveUp")}
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={index === 0}
											onClick={() => move(index, -1)}
											aria-label={t("orderChannelsAdmin.moveUp")}
										>
											<ChevronUp className="h-4 w-4" />
										</Button>
										<Button
											title={t("orderChannelsAdmin.moveDown")}
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={index === items.length - 1}
											onClick={() => move(index, 1)}
											aria-label={t("orderChannelsAdmin.moveDown")}
										>
											<ChevronDown className="h-4 w-4" />
										</Button>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex items-center gap-2">
										<ChannelBrandMark
											name={isAr ? c.nameAr : c.nameEn}
											logo={c.logo}
											color={c.color}
											className="h-9 w-9 text-xs"
										/>
										<div className="leading-tight">
											<div className="font-semibold">{isAr ? c.nameAr : c.nameEn}</div>
											<div className="text-muted-foreground text-xs">
												{isAr ? c.nameEn : c.nameAr}
											</div>
										</div>
									</div>
								</TableCell>
								<TableCell className="text-center">
									<Badge variant="outline">
										{c.channelType === "in_store"
											? t("orderChannelsAdmin.type.inStore")
											: t("orderChannelsAdmin.type.thirdParty")}
									</Badge>
								</TableCell>
								<TableCell className="text-center">
									{c.isDefault ? (
										<Badge variant="default" className="gap-1">
											<Star className="h-3 w-3" />
											{t("orderChannelsAdmin.default")}
										</Badge>
									) : (
										<span className="text-muted-foreground">-</span>
									)}
								</TableCell>
								<TableCell className="text-center">
									<Badge variant={c.isActive ? "success" : "secondary"}>
										{c.isActive ? t("orderChannelsAdmin.active") : t("orderChannelsAdmin.inactive")}
									</Badge>
								</TableCell>
								<TableCell className="text-end">
									<div className="flex justify-end gap-1">
										<PermissionGate permission="settings.update">
											<Button
												title={t("common.edit")}
												aria-label={t("common.edit")}
												variant="ghost"
												size="icon"
												onClick={() => openEdit(c)}
											>
												<Edit className="h-4 w-4" />
											</Button>
										</PermissionGate>
										<PermissionGate permission="settings.delete">
											<Button
												title={t("common.delete")}
												aria-label={t("common.delete")}
												variant="ghost"
												size="icon"
												className="text-destructive"
												onClick={() => handleDelete(c)}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</PermissionGate>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editing ? t("orderChannelsAdmin.editTitle") : t("orderChannelsAdmin.add")}
						</DialogTitle>
						<DialogDescription>{t("orderChannelsAdmin.subtitle")}</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="flex items-center gap-3">
							<ChannelBrandMark
								name={(isAr ? form.nameAr : form.nameEn) || form.nameEn || "?"}
								logo={form.logo || null}
								color={form.color}
								className="h-12 w-12 text-sm"
							/>
							<p className="text-muted-foreground text-xs">{t("orderChannelsAdmin.form.previewHint")}</p>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="oc-nameEn">{t("orderChannelsAdmin.form.nameEn")}</Label>
								<Input
									id="oc-nameEn"
									value={form.nameEn}
									onChange={e => setForm({ ...form, nameEn: e.target.value })}
									placeholder="Talabat"
									required
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="oc-nameAr">{t("orderChannelsAdmin.form.nameAr")}</Label>
								<Input
									id="oc-nameAr"
									value={form.nameAr}
									onChange={e => setForm({ ...form, nameAr: e.target.value })}
									placeholder="طلبات"
									dir="rtl"
									required
								/>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>{t("orderChannelsAdmin.form.channelType")}</Label>
								<Select
									value={form.channelType}
									onValueChange={v =>
										setForm({ ...form, channelType: v as ChannelForm["channelType"] })
									}
								>
									<SelectTrigger aria-label={t("orderChannelsAdmin.form.channelType")}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="in_store">{t("orderChannelsAdmin.type.inStore")}</SelectItem>
										<SelectItem value="third_party">
											{t("orderChannelsAdmin.type.thirdParty")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							{form.channelType === "third_party" ? (
								<div className="space-y-2">
									<Label htmlFor="oc-provider">{t("orderChannelsAdmin.form.provider")}</Label>
									<Input
										id="oc-provider"
										value={form.provider}
										onChange={e => setForm({ ...form, provider: e.target.value })}
										placeholder="talabat"
									/>
								</div>
							) : null}
						</div>

						<div className="space-y-2">
							<Label htmlFor="oc-logo">{t("orderChannelsAdmin.form.logo")}</Label>
							<Input
								id="oc-logo"
								value={form.logo}
								onChange={e => setForm({ ...form, logo: e.target.value })}
								placeholder={t("orderChannelsAdmin.form.logoPlaceholder")}
							/>
						</div>

						<div className="space-y-2">
							<Label>{t("orderChannelsAdmin.form.color")}</Label>
							<div className="flex flex-wrap items-center gap-2">
								{ORDER_TYPE_COLORS.map(col => (
									<button
										key={col}
										type="button"
										onClick={() => setForm({ ...form, color: col })}
										aria-label={col}
										className={`h-8 w-8 rounded-full border-2 transition ${
											form.color === col ? "ring-primary scale-110 ring-2" : "border-transparent"
										}`}
										style={{ backgroundColor: col }}
									/>
								))}
								<Input
									type="color"
									value={form.color}
									onChange={e => setForm({ ...form, color: e.target.value })}
									className="h-8 w-12 p-1"
									aria-label={t("orderChannelsAdmin.form.customColor")}
								/>
							</div>
						</div>

						<div className="flex items-center justify-between rounded-md border p-3">
							<div>
								<Label htmlFor="oc-isDefault">{t("orderChannelsAdmin.form.isDefault")}</Label>
								<p className="text-muted-foreground text-xs">
									{t("orderChannelsAdmin.form.isDefaultHint")}
								</p>
							</div>
							<Switch
								id="oc-isDefault"
								checked={form.isDefault}
								onCheckedChange={checked => setForm({ ...form, isDefault: checked })}
							/>
						</div>

						<div className="flex items-center justify-between rounded-md border p-3">
							<Label htmlFor="oc-isActive">{t("orderChannelsAdmin.form.isActive")}</Label>
							<Switch
								id="oc-isActive"
								checked={form.isActive}
								onCheckedChange={checked => setForm({ ...form, isActive: checked })}
							/>
						</div>

						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
								{t("common.cancel")}
							</Button>
							<Button type="submit" disabled={saving}>
								{editing ? t("common.update") : t("common.create")}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
